import React from 'react';
import { DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface AttachmentChipProps {
  name: string;
  mime?: string;
  size?: number;
  progress?: number; // 0 — 100 while uploading; undefined when settled.
  error?: string;
  onRemove?: () => void;
  className?: string;
}

function humanSize(bytes?: number): string {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const AttachmentChip: React.FC<AttachmentChipProps> = ({
  name,
  size,
  progress,
  error,
  onRemove,
  className,
}) => {
  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-1.5 max-w-[260px] rounded-lg border border-border-secondary bg-surface-secondary ${className ?? ''}`}
    >
      <DocumentIcon className="w-4 h-4 text-text-secondary shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="truncate text-xs font-medium text-text-primary">{name}</p>
        {error ? (
          <p className="text-[11px] text-error-600">{error}</p>
        ) : progress !== undefined && progress < 100 ? (
          <div className="mt-1 h-1 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-[width]"
              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
            />
          </div>
        ) : (
          <p className="text-[11px] text-text-tertiary">{humanSize(size)}</p>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          aria-label="Remove attachment"
          onClick={onRemove}
          className="shrink-0 p-1 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default AttachmentChip;
